// src/pages/products/ManageProductCategoriesPage.jsx
import React, { useState, useEffect } from 'react';
import { 
  FolderTree, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  MoreVertical,
  AlertCircle,
  RefreshCw,
  Save,
  X,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen
} from 'lucide-react';
import { 
  getProductCategories, 
  createProductCategory, 
  getProductCategory, 
  updateProductCategory, 
  deleteProductCategory 
} from '../../services/api/categories';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import styles from './ManageProductCategoriesPage.module.css';

const ManageProductCategoriesPage = () => {
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  
  // State management
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [isSearching, setIsSearching] = useState(false);
  
  // Modal/Form states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCategories, setTotalCategories] = useState(0);
  const [limit] = useState(10);
  
  // Ref for search input to maintain focus
  const searchInputRef = React.useRef(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    parentId: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load categories on component mount and when page changes
  useEffect(() => {
    fetchCategories();
  }, [currentPage]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery !== '') {
        setCurrentPage(1); // Reset to first page when searching
        fetchCategories(true); // Pass true to indicate this is a search operation
      } else if (searchQuery === '') {
        // If search is cleared, fetch all categories
        fetchCategories(true);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCategories = async (isSearchOperation = false) => {
    try {
      if (!isSearchOperation) {
        setIsLoading(true);
      } else {
        setIsSearching(true);
      }
      setLoadingError(null);
      
      const params = {
        page: currentPage,
        limit,
        ...(searchQuery && { search: searchQuery })
      };
      const response = await getProductCategories(params);
      setCategories(response.categories || []);
      setFilteredCategories(response.categories || []);
      setTotalCategories(response.total || 0);
      setTotalPages(Math.ceil((response.total || 0) / limit));
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setLoadingError(t('errors.somethingWentWrong'));
    } finally {
      if (!isSearchOperation) {
        setIsLoading(false);
      } else {
        setIsSearching(false);
        // Restore focus to search input after search completes
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          requestAnimationFrame(() => {
            searchInputRef.current?.focus();
          });
        }
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('categories.nameRequired');
    } else if (formData.name.trim().length < 2) {
      errors.name = t('categories.nameMinLength');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({ name: '', parentId: '' });
    setFormErrors({});
    setEditingCategory(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = async (categoryId) => {
    try {
      setIsSubmitting(true);
      const response = await getProductCategory(categoryId);
      const category = response;
      
      setFormData({
        name: category.name || '',
        parentId: category.parentId || ''
      });
      setEditingCategory(category);
      setShowForm(true);
      setActiveDropdown(null);
    } catch (err) {
      console.error('Failed to fetch category details:', err);
      error(t('categories.failedToLoadCategory'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    const confirmed = await confirm(
      t('categories.confirmDelete', { categoryName }),
      {
        confirmText: t('common.delete'),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) return;

    try {
      await deleteProductCategory(categoryId);
      fetchCategories(); // Refresh the list
      success(t('categories.categoryDeletedSuccess', { categoryName }));
    } catch (err) {
      console.error('Failed to delete category:', err);
      error(t('categories.failedToDelete'));
    }
    setActiveDropdown(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setFormErrors({});

    try {
      const submitData = {
        name: formData.name.trim(),
        ...(formData.parentId && { parentId: parseInt(formData.parentId) })
      };

      if (editingCategory) {
        await updateProductCategory(editingCategory.id, submitData);
        success(t('categories.categoryUpdatedSuccess', { categoryName: submitData.name }));
      } else {
        await createProductCategory(submitData);
        success(t('categories.categoryCreatedSuccess', { categoryName: submitData.name }));
      }

      setShowForm(false);
      resetForm();
      fetchCategories(); // Refresh the list
    } catch (err) {
      console.error('Failed to save category:', err);
      if (err.response?.data?.message) {
        setFormErrors({ submit: err.response.data.message });
      } else {
        setFormErrors({ 
          submit: editingCategory 
            ? t('categories.failedToUpdate') 
            : t('categories.failedToCreate') 
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    resetForm();
  };

  const toggleDropdown = (categoryId) => {
    setActiveDropdown(activeDropdown === categoryId ? null : categoryId);
  };

  const toggleCategoryExpansion = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    // Don't reset page here - let the useEffect handle it
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderCategoryTree = (categoryList, level = 0) => {
    return categoryList.map((category) => (
      <div key={category.id} className={styles.categoryTreeItem}>
        <div className={`${styles.categoryRow} ${styles[`level${Math.min(level, 3)}`]}`}>
          <div className={styles.categoryInfo}>
            <div className={styles.expandButton}>
              {category.children && category.children.length > 0 ? (
                <button
                  onClick={() => toggleCategoryExpansion(category.id)}
                  className={styles.expandToggle}
                >
                  {expandedCategories.has(category.id) ? 
                    <ChevronDown size={16} /> : 
                    <ChevronRight size={16} />
                  }
                </button>
              ) : (
                <div className={styles.expandSpacer}></div>
              )}
            </div>
            
            <div className={styles.categoryIcon}>
              {category.children && category.children.length > 0 ? (
                expandedCategories.has(category.id) ? 
                  <FolderOpen size={16} /> : 
                  <Folder size={16} />
              ) : (
                <Folder size={16} />
              )}
            </div>
            
            <span className={styles.categoryName}>{category.name}</span>
            
            {category.children && category.children.length > 0 && (
              <span className={styles.childrenCount}>
                ({category.children.length})
              </span>
            )}
          </div>
          
          <div className={styles.actionsWrapper}>
            {hasPermission('edit_product_category') && (
              <button 
                onClick={() => handleEdit(category.id)}
                className={`${styles.actionButton} ${styles.editButton}`}
                title={t('common.edit')}
              >
                <Edit size={14} />
              </button>
            )}
            
            {hasPermission('delete_product_category') && (
              <div className={styles.dropdownWrapper}>
                <button
                  className={`${styles.actionButton} ${styles.moreButton}`}
                  onClick={() => toggleDropdown(category.id)}
                  title={t('common.moreActions')}
                >
                  <MoreVertical size={14} />
                </button>
                
                {activeDropdown === category.id && (
                  <div className={styles.dropdownMenu}>
                    <button
                      className={`${styles.dropdownItem} ${styles.deleteItem}`}
                      onClick={() => handleDelete(category.id, category.name)}
                    >
                      <Trash2 size={14} />
                      {t('common.delete')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {category.children && category.children.length > 0 && expandedCategories.has(category.id) && (
          <div className={styles.childrenContainer}>
            {renderCategoryTree(category.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  const flattenCategoriesForSelect = (categoryList, level = 0, prefix = '') => {
    let flattened = [];
    categoryList.forEach(category => {
      // Skip the category being edited to prevent circular reference
      if (!editingCategory || category.id !== editingCategory.id) {
        flattened.push({
          id: category.id,
          name: `${prefix}${category.name}`,
          level
        });
        
        if (category.children && category.children.length > 0) {
          flattened = flattened.concat(
            flattenCategoriesForSelect(category.children, level + 1, `${prefix}${category.name} > `)
          );
        }
      }
    });
    return flattened;
  };

  if (isLoading) {
    return (
      <div className={styles.manageCategoriesPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <FolderTree className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('categories.title')}</h1>
                <p>{t('categories.description')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.loadingState}>
          <div className={styles.loadingSpinnerLarge}></div>
          <h3>{t('categories.loadingCategories')}</h3>
          <p>{t('categories.loadingCategoriesDesc')}</p>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className={styles.manageCategoriesPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <FolderTree className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('categories.title')}</h1>
                <p>{t('categories.description')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.errorState}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h3>{t('errors.somethingWentWrong')}</h3>
          <p>{loadingError}</p>
          <button onClick={fetchCategories} className={styles.retryButton}>
            <RefreshCw size={16} />
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  const flattenedCategories = flattenCategoriesForSelect(categories);

  return (
    <div className={styles.manageCategoriesPage}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <FolderTree className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('categories.title')}</h1>
              <p>{t('categories.description')}</p>
            </div>
          </div>
          
          {hasPermission('create_product_category') && (
            <div className={styles.headerActions}>
              <button onClick={handleCreateNew} className={styles.primaryButton}>
                <Plus size={20} />
                {t('categories.addCategory')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className={styles.pageControls}>
        <div className={styles.searchSection}>
          <div className={styles.searchInputWrapper}>
            <Search className={styles.searchIcon} size={20} />
            <input
              type="text"
              ref={searchInputRef}
              placeholder={t('categories.searchCategories')}
              value={searchQuery}
              onChange={handleSearchChange}
              className={styles.searchInput}
            />
          </div>
        </div>
        
        <div className={styles.controlsRight}>
          <div className={styles.resultsInfo}>
            {isSearching ? (
              <span>Searching...</span>
            ) : (
              <span>{t('categories.totalCategories', { count: totalCategories })}</span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={styles.pageContent}>
        {filteredCategories.length === 0 ? (
          <div className={styles.emptyState}>
            {searchQuery ? (
              <>
                <Search className={styles.emptyIcon} size={48} />
                <h3>{t('categories.noSearchResults')}</h3>
                <p>{t('categories.noSearchResultsDesc')}</p>
                <button 
                  onClick={() => setSearchQuery('')} 
                  className={styles.secondaryButton}
                >
                  {t('common.clearSearch')}
                </button>
              </>
            ) : (
              <>
                <FolderTree className={styles.emptyIcon} size={48} />
                <h3>{t('categories.noCategories')}</h3>
                <p>{t('categories.noCategoriesDesc')}</p>
                {hasPermission('create_product_category') && (
                  <button onClick={handleCreateNew} className={styles.primaryButton}>
                    <Plus size={20} />
                    {t('categories.createFirstCategory')}
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <div className={styles.categoriesTreeContainer}>
              {renderCategoryTree(filteredCategories)}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.paginationContainer}>
                <div className={styles.paginationInfo}>
                  <span>
                    {t('common.showingResults', {
                      start: (currentPage - 1) * limit + 1,
                      end: Math.min(currentPage * limit, totalCategories),
                      total: totalCategories
                    })}
                  </span>
                </div>
                <div className={styles.paginationControls}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={styles.paginationButton}
                  >
                    {t('common.previous')}
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`${styles.paginationButton} ${
                        page === currentPage ? styles.active : ''
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={styles.paginationButton}
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>
                {editingCategory ? t('categories.editCategory') : t('categories.createNewCategory')}
              </h2>
              <button onClick={handleCancelForm} className={styles.modalCloseButton}>
                <X size={20} />
              </button>
            </div>

            {formErrors.submit && (
              <div className={styles.errorBanner}>
                <AlertCircle size={20} />
                <p>{formErrors.submit}</p>
              </div>
            )}

            <form onSubmit={handleFormSubmit} className={styles.modalForm}>
              <div className={styles.formGroup}>
                <label htmlFor="name">{t('categories.categoryName')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('categories.categoryNamePlaceholder')}
                  className={formErrors.name ? 'error' : ''}
                  required
                  autoFocus
                />
                {formErrors.name && <span className={styles.errorText}>{formErrors.name}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="parentId">{t('categories.parentCategory')}</label>
                <select
                  id="parentId"
                  name="parentId"
                  value={formData.parentId}
                  onChange={handleInputChange}
                  className={styles.selectInput}
                >
                  <option value="">{t('categories.selectParentCategory')}</option>
                  {flattenedCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={handleCancelForm}
                  className={styles.cancelButton}
                >
                  <X size={18} />
                  {t('common.cancel')}
                </button>
                <button 
                  type="submit" 
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className={styles.loadingSpinner}></div>
                  ) : (
                    <>
                      <Save size={18} />
                      {editingCategory ? t('categories.updateCategory') : t('categories.createCategory')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProductCategoriesPage;